/* global Shopify */

import {customerContext} from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";
class OrderDetailsPage {
  constructor() {
    this.init();
  }

  init() {
    const orderNumber = window.location.search.split('orderNumber=')[1];
    const orderDetailsPage = document.querySelector('.order-details-page');
    const orderDetailsKeyDetails = orderDetailsPage?.querySelector('.order-details-key-details');
    this.orderDetailsKeyDetails = orderDetailsKeyDetails;
    this.orderDetailsKeyDetailsItemValuesDate = orderDetailsKeyDetails?.querySelector('.order-details-key-details__item-value-date');
    this.orderDetailsKeyDetailsItemValuesStatus = orderDetailsKeyDetails?.querySelector('.order-details-key-details__item-value-status');
    this.orderDetailsKeyDetailsItemValuesCustomerCareRep = orderDetailsKeyDetails?.querySelector('.order-details-key-details__item-value-customer-care-rep');
    this.orderDetailsReorder = orderDetailsPage?.querySelector('.order-details-items__reorder');
    this.orderDetailsReorderButton = orderDetailsPage?.querySelector('.order-details-items__button--reorder');
    this.orderDetailsMultipleRowsSelected = orderDetailsPage?.querySelector('.order-details-items__multiple-rows-selected');
    this.selectAllCheckbox = orderDetailsPage?.querySelector('#selectAll');
    this.reorderRowsSelectedDropdownButton = orderDetailsPage?.querySelector('.order-details-items__multiple-rows-selected-button');

    this.initEligibilityContext();

    const orderDetailsInfo = orderDetailsPage?.querySelector('.order-details-info');
    this.orderDetailsInfo = orderDetailsInfo;
    this.orderDetailsInfoItemValuesOrderInfo = orderDetailsInfo?.querySelector('.order-details-info__item-values--order-info');
    this.orderDetailsInfoItemValuesShippingInfo = orderDetailsInfo?.querySelector('.order-details-info__item-values--shipping-info');
    this.orderDetailsInfoItemValuesPaymentInfo = orderDetailsInfo?.querySelector('.order-details-info__item-values--payment-info');

    const orderDetailsSummary = orderDetailsPage?.querySelector('.order-details-summary');
    this.orderDetailsSummary = orderDetailsSummary;
    this.orderDetailsSummaryItemValuesSubtotal = orderDetailsSummary?.querySelector('.item-value--subtotal');
    this.orderDetailsSummaryItemValuesShipping = orderDetailsSummary?.querySelector('.item-value--shipping');
    this.orderDetailsSummaryItemValuesTax = orderDetailsSummary?.querySelector('.item-value--tax');
    this.orderDetailsSummaryItemValuesTotal = orderDetailsSummary?.querySelector('.item-value--total');

    this.quickAddToCartModal = document.querySelector('.rs-quick-add-to-cart-modal');
    this.orderDetailsTableBody = document.querySelector('.order-details-table__body');
    this.orderDetailsTableLoadingStatus = document.querySelector('#order-details-table-loading-status');

    if (orderNumber) {
      this.getOrderDetails(orderNumber);
    } else {
      this.orderDetailsReorderButton.disabled = true;
      this.orderDetailsReorderButton.classList.add('rs-button--disabled');
      this.orderDetailsReorderButton.setAttribute('aria-disabled', 'true');

      this.renderOrderDetailsTableMessage(window.__t('unable_to_load_order_details'));
      this.finishPageDetailsLoading(true);
    }
  }

  /**
   * Populate company/sales-area fields used by check-eligibility (for variant id + MOQ).
   */
  initEligibilityContext() {
    const ctx = customerContext;
    if (!ctx) return;

    const cand = ctx.sales_area;
    const saRaw = Array.isArray(cand) ? cand[0]?.fields || cand[0] : cand?.fields || cand || {};

    this.companyId = ctx.company_id || null;
    this.locationId = String(ctx.location_id || '').replace('gid://shopify/CompanyLocation/', '') || null;
    this.customerId = document.body.dataset.customerId;
    this.stateCode = ctx.state_code || null;
    this.salesOrg = saRaw?.sales_org_id ?? ctx.sales_org_id ?? null;
    this.distributionChannel = saRaw?.distribution_channel_id ?? ctx.distribution_channel_id ?? null;
    this.division = saRaw?.division_id ?? ctx.division_id ?? null;
  }

  /**
   * @param {string} orderNumber
   */
  async getOrderDetails(orderNumber) {
    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/orders?sap_order_number=${orderNumber}&sold_to=${customerContext?.company_external_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
      });

      if (!response.ok) throw new Error(`Order details request failed with status ${response.status}`);

      const data = await response.json();
      if (!data.orderDetailsResponse) {
        this.renderOrderDetailsTableMessage(window.__t('unable_to_load_order_details'));
        this.finishPageDetailsLoading(true);
        return;
      }

      this.orderDetailsReorderButton.disabled = false;
      this.orderDetailsReorderButton.classList.remove('rs-button--disabled');
      this.orderDetailsReorderButton.setAttribute('aria-disabled', 'false');

      this.populateOrderDetailsKeyDetails(data);
      this.populateOrderDetailsInfoItems(data);
      this.populateOrderDetailsSummary(data);
      await this.populateOrderDetailsItems(data);
      this.initSelectAllCheckbox();
      this.initReorderRowsSelectedDropdown();
    } catch (error) {
      console.error('Error loading order details', error);
      this.renderOrderDetailsTableMessage(window.__t('unable_to_load_order_details'));
      this.finishPageDetailsLoading(true);
    }
  }

  /**
   * @param {{ orderDetailsResponse: { date: string | number | Date; status: string | null; customerCareRepEmail: string | null; }; }} data
   */
  populateOrderDetailsKeyDetails(data) {
    if (!data.orderDetailsResponse) return;

    // Order placed date
    if (data.orderDetailsResponse.date) {
      const date = new Date(data.orderDetailsResponse.date);
      const formattedDate = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
      if (this.orderDetailsKeyDetailsItemValuesDate) this.orderDetailsKeyDetailsItemValuesDate.textContent = formattedDate;
    } else if (this.orderDetailsKeyDetailsItemValuesDate) {
      this.orderDetailsKeyDetailsItemValuesDate.textContent = '—';
    }

    // Order Status
    if (this.orderDetailsKeyDetailsItemValuesStatus) {
      const status = data.orderDetailsResponse.status;
      this.orderDetailsKeyDetailsItemValuesStatus.textContent = status?.toLocaleLowerCase() === 'open' ? window.__t('in_progress') : status || '—';
      if (status) this.orderDetailsKeyDetailsItemValuesStatus.classList.add(`order-details-key-details__item-value-status--${status.toLowerCase()}`);
    }

    // Customer Care Rep
    if (this.orderDetailsKeyDetailsItemValuesCustomerCareRep) {
      const customerCareItem = this.orderDetailsKeyDetailsItemValuesCustomerCareRep.closest('.order-details-key-details__item');
      const customerCareEmail = data.orderDetailsResponse.customerCareRepEmail;
      this.orderDetailsKeyDetailsItemValuesCustomerCareRep.textContent = customerCareEmail || '';
      customerCareItem?.classList.remove('order-details-key-details__item--customer-care-loading');
      customerCareItem?.classList.toggle('hidden', !customerCareEmail);
    }

    this.orderDetailsKeyDetails?.removeAttribute('aria-busy');
  }

  /**
   * @param {{ orderDetailsResponse: { orderInformation: any; }; }} data
   */
  populateOrderDetailsInfoItems(data) {
    if (!data.orderDetailsResponse) return;

    const isShipOrderComplete = data.orderDetailsResponse.isShipOrderComplete ? window.__t('yes') : window.__t('no');

    // Order Information
    const orderInformation = [
      {
        label: window.__t('order_number'),
        value: data.orderDetailsResponse.webOrderNumber,
      },
      {
        label: window.__t('ship_order_complete'),
        value: isShipOrderComplete,
      },
      {
        label: window.__t('sales_order'),
        value: data.orderDetailsResponse.orderNumber,
      },
      {
        label: window.__t('packing_list_notes'),
        value: data.orderDetailsResponse.packingListNotes,
      },
    ];
    const orderInformationItems = this.populateOrderDetails(orderInformation);
    this.orderDetailsInfoItemValuesOrderInfo.innerHTML = orderInformationItems;

    // Shipping Information
    const shippingInformation = [
      {
        label: window.__t('ship_to_location'),
        value: data.orderDetailsResponse.shipTo,
      },
      {
        label: window.__t('shipping_address'),
        value: (() => {
          const addr = data?.orderDetailsResponse?.shippingAddress;
          const line1 = addr?.line1 != null ? addr.line1 : '';
          const line2Parts = [addr?.line2, addr?.city, addr?.region].filter((v) => v != null && v !== '');
          const line3Parts = [addr?.postalCode, addr?.country].filter((v) => v != null && v !== '');
          return [line1, line2Parts.join(', '), line3Parts.join(' ')].filter(Boolean).join('<br> ');
        })(),
      },
      {
        label: window.__t('shipping_method'),
        value: data.orderDetailsResponse.shippingMethod,
      },
      {
        label: window.__t('shipping_instruction'),
        value: data.orderDetailsResponse.shippingInstructions,
      },
    ];
    const shippingInformationItems = this.populateOrderDetails(shippingInformation);
    this.orderDetailsInfoItemValuesShippingInfo.innerHTML = shippingInformationItems;

    // Payment Information
    const paymentMethod = data.orderDetailsResponse.paymentMethod;
    const poNumber = data.orderDetailsResponse.poNumber;
    let paymentMethodValue = '';
    if (paymentMethod) {
      paymentMethodValue = paymentMethod;
    }
    if (poNumber && paymentMethodValue !== 'Credit Card') {
      paymentMethodValue = `Purchase Order <br> PO Number: ${poNumber}`;
    }
    const paymentInformation = [
      {
        label: window.__t('payment_method'),
        value: paymentMethodValue,
      },
      {
        label: window.__t('billing_address'),
        value: (() => {
          const addr = data?.orderDetailsResponse?.billingAddress;
          const line1 = addr?.line1 != null ? addr.line1 : '';
          const line2Parts = [addr?.line2, addr?.city, addr?.region].filter((v) => v != null && v !== '');
          const line3Parts = [addr?.postalCode, addr?.country].filter((v) => v != null && v !== '');
          return [line1, line2Parts.join(', '), line3Parts.join(' ')].filter(Boolean).join('<br> ');
        })(),
      },
    ];
    const paymentInformationItems = this.populateOrderDetails(paymentInformation);
    this.orderDetailsInfoItemValuesPaymentInfo.innerHTML = paymentInformationItems;
    this.orderDetailsInfo?.removeAttribute('aria-busy');
  }

  populateOrderDetails(orderInformation) {
    let items = '';
    orderInformation.forEach((item) => {
      if (item.value) {
        items += `
          <div class="order-details-info__item-value">
            <p class="text-medium text-brand-black weight-600">${item.label}</p>
            <p class="text-medium text-brand-black">${item.value}</p>
          </div>
        `;
      }
    });
    return items;
  }

  /**
   * @param {{ orderDetailsResponse: { items: any[]; }; }} data
   */
  async populateOrderDetailsItems(data) {
    const items = Array.isArray(data.orderDetailsResponse.item) ? data.orderDetailsResponse.item : [];
    const orderDetailsTableBody = this.orderDetailsTableBody;

    if (!orderDetailsTableBody) return;

    if (!items.length) {
      this.renderOrderDetailsTableMessage(window.__t('no_order_items'));
      return;
    }

    // Render the table rows - wait for all async operations to complete
    // Create rows for each delivery of each item
    const allRows = [];

    // First, fetch all shopify variant IDs / qty rules and calculate row indices
    const itemData = await Promise.all(
      items.map(async (item, itemIndex) => {
        const eligibility = item.materialNumber ? await this.getShopifyVariantId(item.materialNumber) : null;
        const shopifyVariantId = eligibility?.shopifyVariantId ?? null;
        const minQty = eligibility?.minQty ?? 1;
        const shipIncrement = eligibility?.shipIncrement ?? 1;
        const deliveries = Array.isArray(item.deliveries) && item.deliveries.length ? item.deliveries : [{}];

        // Calculate starting row index for this item (sum of deliveries from previous items)
        let startingRowIndex = 0;
        for (let i = 0; i < itemIndex; i++) {
          startingRowIndex += Array.isArray(items[i].deliveries) && items[i].deliveries.length ? items[i].deliveries.length : 1;
        }

        return {
          item,
          shopifyVariantId,
          minQty,
          shipIncrement,
          deliveries,
          startingRowIndex,
        };
      })
    );

    // Now create rows for each delivery
    itemData.forEach(({item, shopifyVariantId, minQty, shipIncrement, deliveries, startingRowIndex}) => {
      deliveries.forEach((delivery, deliveryIndex) => {
        const rowIndex = startingRowIndex + deliveryIndex;
        const displayedPartNumber = this.getFirstValue(item.customerPartNumber, item.materialNumber);
        const status = delivery.status;
        const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';
        const openQuantity = delivery.quantity;
        const reorderQuantity = this.resolveReorderQuantity(this.getFirstValue(openQuantity, 1), minQty, shipIncrement);
        const plant = this.getFirstValue(item.plant, delivery.plant, delivery.deliveryPlant);
        const invoicePDFButton = delivery.invoiceNumber ? `<p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Invoice PDF</p><button class='rs-button rs-button-link' data-invoice-number="${this.escapeHTML(delivery.invoiceNumber)}">${this.displayValue(delivery.invoiceNumber)}</button>` : '';
        const itemRow = `
          <tr class="order-details-table__row order-details-table__row-order rs-table-body__row" data-index="${rowIndex + 1}" data-material-number="${this.escapeHTML(item.materialNumber)}">
            <td>
              <div class="rs-checkbox__content rs-mob-checbox-wrapper rs-flex rs-align-center rs-pd-y-4 rs-gap-8">
                <input
                  type="checkbox"
                  id="order-details-item-${rowIndex}"
                  class="rs-checkbox__input"
                  name="order-details-item-${rowIndex}"
                  value=""
                  ${shopifyVariantId ? `` : 'disabled'}
                >
                <span class="rs-flex rs-align-center rs-justify-center rs-border-radius-4 rs-custom-checkbox rs-table-body__checkbox-row">
                  <svg
                    class="rs-icon rs-icon-checkmark"
                    aria-hidden="true"
                    focusable="false"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 48 48"
                  >
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M42.9754 0.62085L48 5.64543L15.7442 37.906L0 21.9179L5.06248 16.9312L15.7821 27.8142L42.9754 0.62085Z" fill="currentColor"></path>
                  </svg>
                </span>
                <label for="order-details-item-${rowIndex}" class="rs-checkbox__label visually-hidden">
                  ${window.__t('order_item_checkbox', { index: rowIndex + 1 })}
                </label>
              </div>
            </td>
            <td class="rs-material-number-input-wrapper"><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Sku/Customer Part Number</p>${this.displayValue(displayedPartNumber)}<input type="hidden" class="order-details-table__sku" value="${this.escapeHTML(item.materialNumber)}" data-product-variant-id="${shopifyVariantId}"></td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Status</p><span class="order-details-table__status status--${this.escapeHTML(normalizedStatus)} text-xsmall weight-500">${normalizedStatus === 'open' ? window.__t('in_progress') : this.displayValue(normalizedStatus)}</span></td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Tracking</p>${this.displayLink(delivery.trackingNumber, delivery.trackingURL)}</td>
            <td>${invoicePDFButton}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Line #</p>${this.displayValue(delivery.deliveryLine)}</td>
            <td class="rs-quantity-input-wrapper"><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">${window.__t('delivery_qty')}</p>${this.displayValue(openQuantity)}<input type="hidden" class="order-details-table__delivery-qty" value="${this.escapeHTML(reorderQuantity)}" data-min="${this.escapeHTML(minQty)}" min="${this.escapeHTML(minQty)}" step="${this.escapeHTML(shipIncrement)}"></td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Price</p>${this.formatCurrency(this.getFirstValue(delivery.totalPrice, item.unitPrice))}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Req Delivery</p>${this.displayDate(item.requiredDeliveryDate)}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Ship Date</p>${this.displayDate(delivery.shipDate)}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Expected Delivery</p>${this.displayDate(delivery.expectedDeliveryDate)}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Default Plant</p>${this.displayValue(plant)}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Delivery /packing list</p>${this.displayValue(delivery.deliveryNumber)}</td>
            <td><p class="text-medium weight-500 text-brand-black rs-desktop-hidden">Carrier</p>${this.displayValue(delivery.carrier)}</td>
          </tr>
        `;

        allRows.push(itemRow);
      });
    });

    // Add all rows to the table body
    orderDetailsTableBody.innerHTML = allRows.join('');
    this.finishOrderDetailsTableLoading();

    // Initialize pagination with total number of rows (deliveries)
    const totalRows = allRows.length;
    this.initPagination(totalRows);

    // Initialize reorder checkboxes
    this.initReorderCheckboxes();

    // Initialize invoice PDF buttons
    this.initInvoicePDFButtons();
  }

  initInvoicePDFButtons() {
    const invoicePDFButtons = document.querySelectorAll('.order-details-table__row-order .rs-button-link');
    invoicePDFButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.getInvoicePDF(button.dataset.invoiceNumber);
      });
    });
  }

  async getInvoicePDF(invoiceNumber) {
    const soldTo = customerContext?.company_external_id || '';
    const response = await fetch(
      `${getCustomerAppBaseUrl()}/api/transactions/document?documentNumber=${invoiceNumber}&documentType=INVOICE&soldTo=${soldTo}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${window.myJWT}`,
        },
      }
    );

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/pdf')) {
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }
    return null;
  }

  finishOrderDetailsTableLoading() {
    this.orderDetailsTableBody?.removeAttribute('aria-busy');
    if (this.orderDetailsTableLoadingStatus) {
      this.orderDetailsTableLoadingStatus.textContent = '';
      this.orderDetailsTableLoadingStatus.removeAttribute('role');
      this.orderDetailsTableLoadingStatus.removeAttribute('aria-live');
    }
  }

  renderOrderDetailsTableMessage(message) {
    if (!this.orderDetailsTableBody) return;
    this.orderDetailsTableBody.innerHTML = `<tr class="order-details-table__message"><td colspan="14">${this.escapeHTML(message)}</td></tr>`;
    this.finishOrderDetailsTableLoading();
  }

  finishPageDetailsLoading(showUnavailable = false) {
    if (showUnavailable) {
      if (this.orderDetailsKeyDetailsItemValuesDate) this.orderDetailsKeyDetailsItemValuesDate.textContent = '—';
      if (this.orderDetailsKeyDetailsItemValuesStatus) this.orderDetailsKeyDetailsItemValuesStatus.textContent = '—';
      const customerCareItem = this.orderDetailsKeyDetailsItemValuesCustomerCareRep?.closest('.order-details-key-details__item');
      customerCareItem?.classList.remove('order-details-key-details__item--customer-care-loading');
      customerCareItem?.classList.add('hidden');
      [this.orderDetailsInfoItemValuesOrderInfo, this.orderDetailsInfoItemValuesShippingInfo, this.orderDetailsInfoItemValuesPaymentInfo].forEach((container) => {
        if (container) container.innerHTML = '<span aria-hidden="true">—</span>';
      });
      [this.orderDetailsSummaryItemValuesSubtotal, this.orderDetailsSummaryItemValuesShipping, this.orderDetailsSummaryItemValuesTax, this.orderDetailsSummaryItemValuesTotal].forEach((container) => {
        if (container) container.textContent = '—';
      });
      const skuCount = document.querySelector('.item-value--sku-count');
      if (skuCount) skuCount.textContent = '';
    }
    this.orderDetailsKeyDetails?.removeAttribute('aria-busy');
    this.orderDetailsInfo?.removeAttribute('aria-busy');
    this.orderDetailsSummary?.removeAttribute('aria-busy');
  }

  getFirstValue(...values) {
    return values.find((value) => value !== undefined && value !== null && value !== '');
  }

  escapeHTML(value) {
    if (value === undefined || value === null) return '';
    return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
  }

  displayValue(value) {
    return this.getFirstValue(value) === undefined ? '&mdash;' : this.escapeHTML(value);
  }

  displayDate(value) {
    // Format the date to MM/DD/YYYY
    const date = new Date(value);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    const newDate = `${month}/${day}/${year}`;
    return value === '0000-00-00' ? '&mdash;' : this.displayValue(newDate);
  }

  displayLink(label, url) {
    if (this.getFirstValue(label) === undefined) return '&mdash;';
    if (this.getFirstValue(url) === undefined) return this.escapeHTML(label);
    return `<a href="${this.escapeHTML(url)}" target="_blank" rel="noopener noreferrer" class="rs-button-link">${this.escapeHTML(label)}</a>`;
  }

  formatCurrency(value) {
    if (this.getFirstValue(value) === undefined) return '&mdash;';
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return this.escapeHTML(value);
    return numericValue.toLocaleString('en-US', {style: 'currency', currency: Shopify.currency.active, minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  /**
   * Ensure reorder qty meets MOQ (and order multiple when bumping to MOQ).
   * @param {any} quantity
   * @param {any} minQty
   * @param {any} shipIncrement
   */
  resolveReorderQuantity(quantity, minQty, shipIncrement) {
    const mn = minQty === 0 ? 1 : Number(minQty) || 1;
    const st = shipIncrement === 0 ? 1 : Number(shipIncrement) || 1;
    let q = Number(quantity);
    if (!Number.isFinite(q) || q < 1) q = 1;

    if (q < mn) {
      let target = mn;
      if (st > 1 && target % st !== 0) {
        target = Math.ceil(target / st) * st;
      }
      return target;
    }

    return q;
  }

  /**
   * @param {any} materialNumber
   * @returns {Promise<{ shopifyVariantId: string, minQty: number, shipIncrement: number } | null | undefined>}
   */
  async getShopifyVariantId(materialNumber) {
    try {
      const token = window.myJWT;
      const payload = this.buildPayload(materialNumber);
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/products/check-eligibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      const result = data.results?.[0];
      const shopifyVariantId = result?.variant_id?.replace('gid://shopify/ProductVariant/', '');
      if (!shopifyVariantId) return null;

      const errors = result.errors || {};
      const minRaw = errors.minOrderQuantity?.value ?? result.minQty;
      const stepRaw = errors.orderMultiple?.value ?? result.shipIncrement;
      const minQty = minRaw === 0 ? 1 : Number(minRaw) || 1;
      const shipIncrement = stepRaw === 0 ? 1 : Number(stepRaw) || 1;

      return {shopifyVariantId, minQty, shipIncrement};
    } catch (error) {
      console.error('Error getting Shopify variant ID', error);
    }
  }

  /**
   * @param {any} sku
   */
  buildPayload(sku) {
    return {
      company_id: this.companyId || null,
      company_location_gid: this.locationId || null,
      customer_gid: this.customerId || null,
      location_id: this.locationId || null,
      state_code: this.stateCode || null,
      sales_organization: this.salesOrg,
      distribution_channel: this.distributionChannel,
      division: this.division,
      products: [
        {
          sku: sku,
          quantity: 1,
        },
      ],
    };
  }

  initPagination(totalItems) {
    const paginationWrapper = document.querySelector('.order-details-pagination-wrapper');
    if (!paginationWrapper) return;

    // Show pagination if there are items
    if (totalItems > 0) {
      paginationWrapper.style.display = 'flex';
    } else {
      paginationWrapper.style.display = 'none';
      return;
    }

    const rows = document.querySelectorAll('.order-details-table__row-order');
    const optionButtons = document.querySelectorAll('.order-details-pagination__rows-options .rs-cart-pagination__rows-option');
    const selectedOptionBtn = document.querySelector('.order-details-pagination__rows-options .rs-cart-pagination__rows-option.rs-active');
    const rowSelectBtn = document.querySelector('.order-details-pagination__rows-button');
    const prevBtn = document.getElementById('order-details-pagination-prev');
    const nextBtn = document.getElementById('order-details-pagination-next');
    const pageInfo = document.querySelector('.order-details-pagination__info');
    const rowsOptions = document.querySelector('.order-details-pagination__rows-options');

    if (!rows.length || !optionButtons.length || !prevBtn || !nextBtn || !pageInfo) return;

    let currentPage = 1;
    let rowsPerPage = parseInt(selectedOptionBtn?.value || '10', 10);
    let totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

    function renderPage() {
      totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

      if (currentPage > totalPages) currentPage = totalPages;
      if (currentPage < 1) currentPage = 1;

      const startIndex = (currentPage - 1) * rowsPerPage + 1;
      const endIndex = Math.min(startIndex + rowsPerPage - 1, totalItems);

      rows.forEach((row) => {
        const idx = parseInt(row.dataset.index, 10);
        if (idx >= startIndex && idx <= endIndex) {
          row.classList.remove('rs-hidden');
        } else {
          row.classList.add('rs-hidden');
        }
      });

      pageInfo.textContent = `${startIndex}-${endIndex} of ${totalItems}`;
      prevBtn.disabled = currentPage === 1;
      nextBtn.disabled = currentPage === totalPages;
    }

    // Add event listeners for pagination buttons
    nextBtn.addEventListener('click', () => {
      if (currentPage < totalPages) {
        currentPage++;
        renderPage();
      }
    });

    prevBtn.addEventListener('click', () => {
      if (currentPage > 1) {
        currentPage--;
        renderPage();
      }
    });

    optionButtons.forEach((option) => {
      option.addEventListener('click', () => {
        optionButtons.forEach((btn) => btn.classList.remove('rs-active'));
        option.classList.add('rs-active');
        rowsPerPage = parseInt(option.value, 10);
        currentPage = 1;
        if (rowSelectBtn) {
          rowSelectBtn.querySelector('span').textContent = window.__t('show_count_rows', { count: option.value });
        }
        renderPage();
      });
    });

    // Handle dropdown toggle
    if (rowSelectBtn && rowsOptions) {
      rowSelectBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = rowSelectBtn.getAttribute('aria-expanded') === 'true';
        rowSelectBtn.setAttribute('aria-expanded', String(!isExpanded));
        rowsOptions.style.display = isExpanded ? 'none' : 'block';
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!rowSelectBtn.contains(e.target) && !rowsOptions.contains(e.target)) {
          rowSelectBtn.setAttribute('aria-expanded', 'false');
          rowsOptions.style.display = 'none';
        }
      });
    }

    renderPage();
  }

  populateOrderDetailsSummary(data) {
    const zero = 0;

    const subtotal = data.orderDetailsResponse.subTotalValue.toLocaleString('en-US', {style: 'currency', currency: Shopify.currency.active}, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const shipping = data.orderDetailsResponse.shippingValue === 0 || data.orderDetailsResponse.shippingValue === null ? 'Free' : data.orderDetailsResponse.shippingValue.toLocaleString('en-US', {style: 'currency', currency: Shopify.currency.active}, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const tax = data.orderDetailsResponse.taxValue === 0 || data.orderDetailsResponse.taxValue === null ? zero.toLocaleString('en-US', {style: 'currency', currency: Shopify.currency.active}, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : data.orderDetailsResponse.taxValue.toLocaleString('en-US', {style: 'currency', currency: Shopify.currency.active}, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const total = data.orderDetailsResponse.totalNetValue.toLocaleString('en-US', {style: 'currency', currency: Shopify.currency.active}, {minimumFractionDigits: 2, maximumFractionDigits: 2});

    this.orderDetailsSummaryItemValuesSubtotal.textContent = subtotal;
    this.orderDetailsSummaryItemValuesShipping.textContent = shipping;
    this.orderDetailsSummaryItemValuesTax.textContent = tax;
    this.orderDetailsSummaryItemValuesTotal.textContent = total;

    const skuCount = Array.isArray(data.orderDetailsResponse.item) ? data.orderDetailsResponse.item.length : 0;
    document.querySelector('.item-value--sku-count').textContent = window.__t('skus_count', { count: skuCount });
    this.orderDetailsSummary?.removeAttribute('aria-busy');
  }

  initReorderCheckboxes() {
    const reorderCheckboxes = document.querySelectorAll('.order-details-table__row-order .rs-checkbox__input');

    reorderCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const row = checkbox.closest('.order-details-table__row-order');

        // Keep row selection state in sync with the checkbox
        row?.classList.toggle('rs-selected', checkbox.checked);

        // Update the reorder button state according to if there is a selected row
        this.updateReorderButtonState();

        // Update the select all checkbox state
        this.updateSelectAllCheckboxState();
      });
    });
  }

  updateSelectAllCheckboxState() {
    if (!this.selectAllCheckbox) return;

    const tableCheckboxes = document.querySelectorAll(
      '.order-details-table__row-order .rs-checkbox__input:not([disabled])'
    );

    const selectedCheckboxes = Array.from(tableCheckboxes).filter(
      (checkbox) => checkbox.checked
    );

    const totalRows = tableCheckboxes.length;
    const selectedRows = selectedCheckboxes.length;

    if (totalRows === 0) {
      this.selectAllCheckbox.checked = false;
      this.selectAllCheckbox.indeterminate = false;
      this.selectAllCheckbox.setAttribute('aria-checked', 'false');
    } else if (selectedRows === totalRows) {
      // All eligible rows selected
      this.selectAllCheckbox.checked = true;
      this.selectAllCheckbox.indeterminate = false;
      this.selectAllCheckbox.setAttribute('aria-checked', 'true');
    } else if (selectedRows > 0) {
      // Some, but not all, eligible rows selected
      this.selectAllCheckbox.checked = false;
      this.selectAllCheckbox.indeterminate = true;
      this.selectAllCheckbox.setAttribute('aria-checked', 'mixed');
    } else {
      // No rows selected
      this.selectAllCheckbox.checked = false;
      this.selectAllCheckbox.indeterminate = false;
      this.selectAllCheckbox.setAttribute('aria-checked', 'false');
    }

    this.selectAllCheckbox
      .closest('.rs-checkbox__content')
      ?.classList.toggle('all-rows-selected', selectedRows === totalRows && totalRows > 0);
  }

  updateReorderButtonState() {
    // Check if any rows are selected
    const selectedRows = document.querySelectorAll('.order-details-table__row-order.rs-selected');
    if (selectedRows.length > 0) {
      // Add the blue gradient background
      this.orderDetailsReorder.classList.add('bg-status-blue-gradient');

      // Show the multiple rows selected button
      this.orderDetailsMultipleRowsSelected.classList.remove('rs-hidden');

      // Update the multiple rows selected count
      this.orderDetailsMultipleRowsSelected.querySelector('.order-details-items__multiple-rows-selected-count').textContent = selectedRows.length;

      // Update the select all checkbox presentation
        if (selectedRows.length > 0) {
        // Add the blue gradient background
        this.orderDetailsReorder.classList.add('bg-status-blue-gradient');

        // Show the multiple rows selected button
        this.orderDetailsMultipleRowsSelected.classList.remove('rs-hidden');

        // Update the multiple rows selected count
        this.orderDetailsMultipleRowsSelected.querySelector('.order-details-items__multiple-rows-selected-count').textContent = selectedRows.length;
      }
    } else {
      // Remove the blue gradient background
      this.orderDetailsReorder.classList.remove('bg-status-blue-gradient');

      // Hide the multiple rows selected button
      this.orderDetailsMultipleRowsSelected.classList.add('rs-hidden');

      // Update the multiple rows selected count
      this.orderDetailsMultipleRowsSelected.querySelector('.order-details-items__multiple-rows-selected-count').textContent = '0';
    }
  }

  initSelectAllCheckbox() {
    if (this.selectAllCheckbox) {
      this.selectAllCheckbox.addEventListener('change', () => {
        const tableRows = document.querySelectorAll('.order-details-table__row-order');

        tableRows.forEach((row) => {
          const checkbox = row.querySelector('.rs-checkbox__input');

          // Only update eligible/enabled row checkboxes
          if (!checkbox || checkbox.disabled) return;

          checkbox.checked = this.selectAllCheckbox.checked;
          row.classList.toggle('rs-selected', checkbox.checked);
        });

        // Header was explicitly changed by the user,
        // so clear the indeterminate state.
        this.selectAllCheckbox.indeterminate = false;
        this.selectAllCheckbox.setAttribute(
          'aria-checked',
          this.selectAllCheckbox.checked ? 'true' : 'false'
        );

        this.updateReorderButtonState();
        this.updateSelectAllCheckboxState();
      });
    }
  }

  initReorderRowsSelectedDropdown() {
    if (this.reorderRowsSelectedDropdownButton) {
      this.reorderRowsSelectedDropdownButton.addEventListener('click', () => {
        const dropdown = document.querySelector('.order-details-items__options');
        if (dropdown) {
          dropdown.classList.toggle('rs-hidden');
        }
      });

      // Select all
      const selectAllButton = document.querySelector('.order-details-items__option--select-all');
      if (selectAllButton) {
        selectAllButton.addEventListener('click', () => {
          const tableRows = document.querySelectorAll('.order-details-table__row-order');
          tableRows.forEach((row) => {
            row.classList.add('rs-selected');
            row.querySelector('.rs-checkbox__input').checked = true;
          });

          this.updateReorderButtonState();
          this.updateSelectAllCheckboxState();
        });
      }

      // Unselect all
      const unselectAllButton = document.querySelector('.order-details-items__option--unselect-all');
      if (unselectAllButton) {
        unselectAllButton.addEventListener('click', () => {
          const tableRows = document.querySelectorAll('.order-details-table__row-order');
          tableRows.forEach((row) => {
            row.classList.remove('rs-selected');
            row.querySelector('.rs-checkbox__input').checked = false;
          });

          this.updateReorderButtonState();
          this.updateSelectAllCheckboxState();
        });
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const checkJWT = () => {
    if (window.myJWT) {
      new OrderDetailsPage();
    } else {
      setTimeout(checkJWT, 100);
    }
  };
  checkJWT();
});
